import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import AdminSidebar from "./adminSidebar";
import AdminHeader from "./adminHeader";
import { useSessionTimeout } from "../hooks/useSessionTimeout";
import { isTokenExpired, isValidTokenFormat } from "../utils/tokenUtils";

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const location = useLocation();
  
  // Enable session timeout monitoring
  useSessionTimeout('admin');
  
  // CRITICAL: Additional security check - Validate admin authentication properly
  // This works alongside AdminAuthGuard as a defensive layer
  const adminAuthToken = localStorage.getItem("adminAuthToken");
  const adminData = localStorage.getItem("adminData");
  const isLoginPage = location.pathname.startsWith("/admin/login");
  
  // Comprehensive authentication check
  let isAuthenticated = false;
  if (adminAuthToken && adminData) {
    // Validate token format
    if (isValidTokenFormat(adminAuthToken)) {
      // Check if token is expired
      if (!isTokenExpired(adminAuthToken)) {
        // All checks passed
        isAuthenticated = true;
      }
    }
  }
  
  // If not authenticated and not on login page, redirect to login
  if (!isAuthenticated) {
    if (!isLoginPage) {
      // Clear any stale/invalid auth data
      localStorage.removeItem("adminAuthToken");
      localStorage.removeItem("adminRefreshToken");
      localStorage.removeItem("adminAuth");
      localStorage.removeItem("adminData");
      return <Navigate to="/admin/login" replace />;
    }
    return null; // Already on login page, let it render
  }

  // Handle window resize
  useEffect(() => {
    let previousWidth = window.innerWidth;
    
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const mobile = currentWidth < 992;
      const wasMobile = previousWidth < 992;
      
      setIsMobile(mobile);
      
      // Only auto-close when switching FROM desktop TO mobile, not when sidebar state changes
      if (mobile && !wasMobile) {
        setSidebarOpen((prev) => prev ? false : prev);
      }
      
      previousWidth = currentWidth;
    };

    window.addEventListener('resize', handleResize);
    // Initial check - only close if already mobile on mount
    if (window.innerWidth < 992 && sidebarOpen) {
      setSidebarOpen(false);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array - only run on mount/unmount

  // Close sidebar when clicking overlay on mobile
  const handleOverlayClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <div style={{
      display: "flex",
      minHeight: "100vh",
      background: "linear-gradient(120deg,#eef5fb 0%,#e8e9ff 100%)",
      position: "relative",
    }}>
      {/* Overlay for mobile sidebar */}
      {isMobile && sidebarOpen && (
        <div
          className="admin-sidebar-overlay active"
          onClick={handleOverlayClick}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 99,
            display: "block",
            transition: "opacity 0.3s ease",
          }}
        />
      )}
      
      <AdminSidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen}
        isMobile={isMobile}
      />
      
      <div
        className="admin-main-content"
        style={{
          flex: 1,
          marginLeft: isMobile ? 0 : (sidebarOpen ? 210 : 70),
          transition: ".2s margin",
          display: "flex",
          flexDirection: "column",
          width: isMobile ? "100%" : "auto",
          minWidth: 0,
        }}
      >
        <AdminHeader 
          sidebarOpen={sidebarOpen} 
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
        />
        <div 
          className="admin-content-padding"
          style={{ 
            padding: isMobile ? "15px 10px" : "34px 32px", 
            minHeight: 0, 
            flex: 1,
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
}
